import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, DatePicker, EmptyState, PageHeader, Segmented, Stack, t, Tabs, Text, toast } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { Shell } from '../components/Shell';
import { ListTable } from '../components/ListTable';
import { RefusalPage } from '../components/Refusal';
import { api, unwrap } from '../lib/api';
import { duration, percent, Period, PERIODS, periodBounds } from '../lib/availability';
import { useSession } from '../lib/session';

const monthName = (month: string) => new Date(`${month}-01T12:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

type Row = fleetState.paths['/availability']['get']['responses']['200']['content']['application/json']['items'][number];

/**
 * « Disponibilité » (D44, docs/01-specifications/technical/10-disponibilite.md),
 * DSM index page: the period as saved views (Segmented), then one DataTable
 * of the servers with their availability, the coverage of its measure, their
 * interruptions and scheduled stops; export CSV. A figure measured less than
 * 95 % of the time says « Mesure insuffisante » instead of a percentage.
 */
export function AvailabilityPage() {
  const navigate = useNavigate();
  const { can } = useSession();
  // Servers (host UP in Checkmk) or applications (the external probes), one tab each; the tab is in the address.
  const [params, setParams] = useSearchParams();
  const kind = params.get('kind') === 'application' ? 'application' : 'server';
  const [period, setPeriod] = useState<Period>('month');
  const [custom, setCustom] = useState<[Date | null, Date | null]>([null, null]);
  const bounds = periodBounds(period, custom);
  const list = useQuery({
    queryKey: ['availability', kind, bounds?.from, period === 'custom' ? bounds?.to : period],
    // The period's threshold and its total (the export); the rows are the list below (D120).
    queryFn: async () => unwrap(await api.fleetState.GET('/availability', { params: { query: { kind, from: bounds!.from, to: bounds!.to, pageSize: 1 } } })),
    enabled: Boolean(bounds),
    placeholderData: keepPreviousData,
    refetchInterval: period === 'custom' ? false : 60_000,
  });
  // § 3: the last monthly check against Checkmk's history (whole-fleet people only; others get 404).
  const check = useQuery({
    queryKey: ['availability-check'],
    queryFn: async () => unwrap(await api.fleetState.GET('/availability/check')),
    retry: false,
  });
  const crumbs = [{ label: t('nav.availability') }];
  if (list.error) return <Shell crumbs={crumbs}><RefusalPage error={list.error} /></Shell>;

  const exportCsv = () => {
    window.location.assign(`/api/fleet-state/availability/export?${new URLSearchParams({ kind, ...bounds! }).toString()}`);
    toast({ title: t('availability.exported'), tone: 'info' });
  };
  const figureOf = (r: Row) => {
    if (r.availability === null) return <Text variant="body-sm" tone="mute">{t('availability.notMeasured')}</Text>;
    if (!r.sufficient) return <Badge tone="warn" size="sm">{t('availability.insufficient')}</Badge>;
    return percent(r.availability);
  };
  return (
    <Shell crumbs={crumbs}>
      <Stack gap={24}>
        <PageHeader title={t('availability.title')} subtitle={t('availability.subtitle', { threshold: percent(list.data?.threshold ?? 0.95, 0) })} />
        {check.data?.check && (check.data.check.gaps.length
          ? <Alert tone="warn" title={t('availability.check.gapsTitle', { month: monthName(check.data.check.month), n: check.data.check.gaps.length })}>
              {t('availability.check.gaps', { servers: check.data.check.gaps.map(g => `${g.server} (${duration(g.portalSeconds)} / ${duration(g.coreSeconds)})`).join(', ') })}
            </Alert>
          : <Text variant="body-sm" tone="mute">{t('availability.check.none', { month: monthName(check.data.check.month), n: check.data.check.servers })}</Text>)}
        <Tabs label={t('availability.kinds')} value={kind} onChange={id => setParams(id === 'application' ? { kind: 'application' } : {})}
          items={[{ id: 'server', label: t('availability.kind.server') }, { id: 'application', label: t('availability.kind.application') }]} />
        <Segmented label={t('availability.periods')} size="sm" value={period} onChange={v => setPeriod(v as Period)}
          items={PERIODS.map(id => ({ id, label: t(`availability.period.${id}`) }))} />
        {/* D120: the server searches, filters, sorts and pages; the CSV keeps every row. */}
        <ListTable<Row>
          label={t(`availability.table.${kind}`)}
          queryKey={['availability-rows', kind, bounds?.from, period === 'custom' ? bounds?.to : period]}
          fetch={async query => unwrap(await api.fleetState.GET('/availability', { params: { query: { ...query, kind, from: bounds!.from, to: bounds!.to } as never } }))}
          enabled={Boolean(bounds)}
          refetchInterval={period === 'custom' ? false : 60_000}
          rowKey="object"
          defaultSort={{ key: 'availability', dir: 'asc' }}
          search={t(`availability.search.${kind}`)}
          facets={kind === 'server' ? [{ key: 'environment', label: t('availability.columns.environment') }] : []}
          // DSM: the period's own control sits at the head of the toolbar.
          toolbarExtra={period === 'custom'
            ? <DatePicker range size="sm" ariaLabel={t('availability.customLabel')} placeholder={t('availability.customLabel')} value={custom} max={new Date()} onChange={v => setCustom(v as [Date | null, Date | null])} />
            : undefined}
          onRowClick={r => navigate(kind === 'server' ? `/servers/${encodeURIComponent(r.object)}?tab=history` : `/applications/${encodeURIComponent(r.object)}?tab=monitoring`)}
          toolbarActions={can('server.export') ? <Button variant="secondary" size="sm" icon="download" onClick={exportCsv} disabled={!bounds || (list.data?.total ?? 0) === 0}>{t('availability.export')}</Button> : undefined}
          columns={[
            kind === 'server'
              ? { key: 'object', label: t('availability.columns.server'), mono: true, minWidth: 200, grow: true, pinned: 'left' as const }
              : { key: 'title', label: t('availability.columns.application'), minWidth: 220, grow: true, pinned: 'left' as const },
            { key: 'environment', label: t('availability.columns.environment'), width: 140, hidden: kind === 'application' },
            { key: 'availability', label: t('availability.columns.availability'), width: 170, align: 'right' as const, render: figureOf },
            { key: 'coverage', label: t('availability.columns.coverage'), description: t('availability.columns.coverageHelp'), width: 130, align: 'right' as const,
              render: (r: Row) => percent(r.coverage, 1) },
            { key: 'interruptions', label: t('availability.columns.interruptions'), width: 130, align: 'right' as const },
            { key: 'interruptionSeconds', label: t('availability.columns.down'), width: 140, align: 'right' as const, render: (r: Row) => duration(r.interruptionSeconds) },
            { key: 'scheduled', label: t('availability.columns.scheduled'), width: 150, align: 'right' as const, render: (r: Row) => duration(r.seconds.scheduled) },
            { key: 'unmeasured', label: t('availability.columns.unmeasured'), width: 130, align: 'right' as const, hidden: true, render: (r: Row) => duration(r.seconds.unmeasured) },
          ]}
          empty={<EmptyState framed={false} icon="monitoring" title={period === 'custom' && !bounds ? t('availability.empty.pick') : t(`availability.empty.${kind}`)} />}
        />
      </Stack>
    </Shell>
  );
}
